import { useEffect, useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import * as Select from '@radix-ui/react-select';
import { useTheme } from '../../context/ThemeContext';
import { useFieldLabel } from '../../hooks/useFieldLabel';

interface AddPeopleToGroupModalProps {
  open: boolean;
  onClose: () => void;
  selectedUsers: any[];
  groups: any[];
  onSave: (payload: {
    groupId: number;
    members: {
      userId: string;
      isAdmin: boolean;
    }[];
  }) => void;
}

interface ModalSelectProps {
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onValueChange: (value: string) => void;
  theme: ReturnType<typeof useTheme>['theme'];
}

function ModalSelect({
  value,
  placeholder,
  options,
  onValueChange,
  theme,
}: ModalSelectProps) {
  return (
    <Select.Root value={value} onValueChange={onValueChange}>
      <Select.Trigger
        className="flex min-h-11 w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm outline-none focus:ring-2 focus:ring-offset-1"
        style={{
          backgroundColor: theme.surface,
          borderColor: theme.border,
          color: theme.text,
        }}
        aria-label={placeholder}
      >
        <Select.Value className="min-w-0 flex-1 whitespace-normal break-all" placeholder={placeholder} />
        <Select.Icon asChild>
          <ChevronDown
            className="h-4 w-4 shrink-0"
            style={{ color: theme.textSecondary }}
          />
        </Select.Icon>
      </Select.Trigger>

      <Select.Content
        position="popper"
        side="bottom"
        sideOffset={4}
        className="z-20 max-h-52 overflow-hidden rounded-lg border shadow-lg"
        style={{
          width: 'var(--radix-select-trigger-width)',
          backgroundColor: theme.surface,
          borderColor: theme.border,
          color: theme.text,
        }}
      >
        <Select.Viewport className="max-h-52 overflow-y-auto p-1">
          {options.map((option) => (
            <Select.Item
              key={option.value}
              value={option.value}
              className="flex w-full min-h-9 cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none data-[highlighted]:bg-black/10"
            >
              <Select.ItemText className="min-w-0 flex-1">
                <span className="block whitespace-normal break-all">
                  {option.label}
                </span>
              </Select.ItemText>
            </Select.Item>
          ))}
        </Select.Viewport>
      </Select.Content>
    </Select.Root>
  );
}

export default function AddPeopleToGroupModal({
  open,
  onClose,
  selectedUsers,
  groups,
  onSave,
}: AddPeopleToGroupModalProps) {
  const listLabel = useFieldLabel('MENU_LIST', 'List');
  const { theme } = useTheme();

  const [groupTypes, setGroupTypes] = useState<string[]>([]);
  const [selectedGroupType, setSelectedGroupType] = useState('');

  const [filteredGroups, setFilteredGroups] = useState<any[]>([]);

  const [selectedGroupId, setSelectedGroupId] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedGroupType('');
      setSelectedGroupId('');
      setFilteredGroups([]);
    }
  }, [open]);

  useEffect(() => {
    if (groups?.length > 0) {
      const uniqueTypes = [
        ...new Set(
          groups
            .filter((x) => x.groupType)
            .map((x) => x.groupType.trim().toLowerCase())
        ),
      ].map((x) => x.charAt(0).toUpperCase() + x.slice(1));

      setGroupTypes(uniqueTypes);
    }
  }, [groups]);

  const handleGroupTypeChange = (value: string) => {
    setSelectedGroupType(value);

    setSelectedGroupId('');

    const filtered = groups.filter(
      (x) => x.groupType && x.groupType.toLowerCase() === value.toLowerCase()
    );

    setFilteredGroups(filtered);
  };

  const handleSave = () => {
    if (!selectedGroupId) {
      return;
    }

    const payload = {
      groupId: Number(selectedGroupId),

      members: selectedUsers.map((user) => ({
        userId: user.userId,
        isAdmin: false,
      })),
    };

    onSave(payload);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="w-full max-w-md rounded-2xl shadow-xl overflow-hidden"
        style={{
          backgroundColor: theme.surface,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-5 border-b"
          style={{
            borderColor: theme.border,
          }}
        >
          <h2 className="text-xl font-semibold" style={{ color: theme.text }}>
            Add People To {listLabel}
          </h2>

          <button onClick={onClose}>
            <X className="w-5 h-5" style={{ color: theme.text }} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Selected Users */}
          <div>
            <label
              className="text-sm font-medium mb-2 block"
              style={{ color: theme.text }}
            >
              Selected Users
            </label>

            <div
              className="flex flex-wrap gap-2 max-h-28 overflow-y-auto border rounded-lg p-3"
              style={{
                borderColor: theme.border,
              }}
            >
              {selectedUsers.map((user: any) => (
                <div
                  key={user.userId}
                  className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: theme.primary,
                    color: theme.contrastText,
                  }}
                >
                  {user.firstName} {user.lastName}
                </div>
              ))}
            </div>
          </div>

          {/* Group Type */}
          <div>
            <label
              className="text-sm font-medium mb-2 block"
              style={{ color: theme.text }}
            >
              {listLabel} Type
            </label>

            <div className="relative">
              <ModalSelect
                value={selectedGroupType}
                placeholder={`Select ${listLabel} Type`}
                options={groupTypes.map((type) => ({
                  value: type,
                  label: type,
                }))}
                onValueChange={handleGroupTypeChange}
                theme={theme}
              />
            </div>
          </div>

          {/* Group */}
          <div>
            <label
              className="text-sm font-medium mb-2 block"
              style={{ color: theme.text }}
            >
              {listLabel}
            </label>

            <div className="relative">
              <ModalSelect
                value={selectedGroupId}
                placeholder={`Select ${listLabel}`}
                options={filteredGroups.map((group) => ({
                  value: String(group.groupId),
                  label: group.groupName,
                }))}
                onValueChange={setSelectedGroupId}
                theme={theme}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-3 p-5 border-t"
          style={{
            borderColor: theme.border,
          }}
        >
          <button
            onClick={onClose}
            className="h-10 px-5 rounded-lg border text-sm font-medium"
            style={{
              borderColor: theme.border,
              color: theme.text,
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            className="h-10 px-5 rounded-lg text-sm font-medium"
            style={{
              backgroundColor: theme.primary,
              color: theme.contrastText,
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
